import type { ApiError } from '@fos/contracts';

/** Error of the API (ch. 13.2): code + translated message + field paths. */
export class ApiFailure extends Error {
  constructor(
    readonly status: number,
    readonly code: ApiError['error']['code'] | 'NETWORK',
    message: string,
    readonly fields: Array<{ path: string; code: string }> = [],
    readonly details: Record<string, unknown> = {},
  ) {
    super(message);
  }
}

export interface CallOptions {
  body?: unknown;
  /** If-Match (ch. 13.5) */
  version?: number;
  query?: Record<string, string | number | boolean | undefined | null>;
  signal?: AbortSignal;
}

export async function api<T>(method: 'GET' | 'POST' | 'PATCH' | 'DELETE', path: string, opts: CallOptions = {}): Promise<T> {
  const qs = opts.query
    ? '?' +
      new URLSearchParams(
        Object.entries(opts.query)
          .filter(([, v]) => v !== undefined && v !== null && v !== '')
          .map(([k, v]) => [k, String(v)]),
      ).toString()
    : '';
  let res: Response;
  try {
    res = await fetch(path + (qs === '?' ? '' : qs), {
      method,
      credentials: 'same-origin',
      headers: {
        accept: 'application/json',
        ...(method !== 'GET' ? { 'x-fos-csrf': '1' } : {}),
        ...(opts.body !== undefined ? { 'content-type': 'application/json' } : {}),
        ...(opts.version !== undefined ? { 'if-match': String(opts.version) } : {}),
      },
      body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
      signal: opts.signal,
    });
  } catch {
    throw new ApiFailure(0, 'NETWORK', '');
  }
  if (res.status === 204) return undefined as T;
  const json = (await res.json().catch(() => null)) as unknown;
  if (!res.ok) {
    const e = (json as ApiError | null)?.error;
    throw new ApiFailure(res.status, e?.code ?? 'INTERNAL', e?.message ?? '', e?.fields ?? [], e?.details ?? {});
  }
  return json as T;
}

export const isFailure = (e: unknown, code?: string): e is ApiFailure => e instanceof ApiFailure && (!code || e.code === code);

/** Opens a PDF from the API; false when the PDF service is not available (A5: never show raw JSON). */
export async function openPdf(path: string): Promise<boolean> {
  const win = window.open('', '_blank');
  try {
    const res = await fetch(path, { credentials: 'same-origin' });
    if (!res.ok || !(res.headers.get('content-type') ?? '').includes('pdf')) throw new Error('pdf');
    const url = URL.createObjectURL(await res.blob());
    if (win) win.location.href = url;
    else window.location.assign(url);
    return true;
  } catch {
    win?.close();
    return false;
  }
}
